import { AppRegistrationSection } from '../../AppRegistrationSection';
import {
  ArrowLeftIcon,
  DocumentPhotoIcon,
  InformationIcon,
  LockIcon,
  UploadCloudDisabledIcon,
  UploadCloudIcon,
  WarningRedIcon,
} from 'components/icons';
import {
  Button,
  ButtonActor,
  Card,
  Link,
  Modal,
  Select,
  Spacer,
  Text,
  Title,
} from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getProcessEnvUrl } from '../../utils/getUrl';
import { getScreenTranslation } from '../../utils/translations';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { routes } from '../../routes';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotifications } from 'InternalLibrary';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import classes from 'classnames';
import classNames from 'classnames';
import layout from 'scss/layout.module.scss';
import Loader from 'components/Loader/Loader';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import RegistrationLoading from '../RegistrationLoading/RegistrationLoading';
import styles from 'pages/RegistrationUploadDoc/RegistrationUploadDoc.module.scss';
import UploadCard, {
  DOC_TYPE,
  DOCUMENTS_TYPES_LIST,
  UploadCardProps,
} from 'components/atoms/UploadCard/UploadCard';
import useClient from '../../hooks/useClient';

// Imports
import heic2any from 'heic2any';

type FormikField = 'document' | 'idBack' | 'idFront';

const MAX_FILE_SIZE = 25000000;
const VALID_FILE_TYPES = ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'pdf'];
const DESTINATION_FILE_TYPE = 'image/jpeg';

interface FormFormik {
  document: null | File;
  idBack: null | File;
  idFront: null | File;
}

interface AdditionalDocModalInfoState {
  step: number;
  docType: DOC_TYPE | null;
  doc: File | null;
}

export default function RegistrationUploadDoc() {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleError } = useNotifications();
  const { fetchClient } = useClient();
  const i18Next = useTranslation('register_upload_documentation');
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalAddDocLoading, setModalAddDocLoading] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [additionalDocModalInfo, setAdditionalDocModalInfo] =
    useState<AdditionalDocModalInfoState>({
      step: 0,
      docType: null,
      doc: null,
    });

  const content = getScreenTranslation(
    'register_upload_documentation',
    i18Next,
  );

  const DOC_TYPES = DOCUMENTS_TYPES_LIST.map((idDocType) => ({
    name: content.documentTypes[
      idDocType as keyof typeof content.documentTypes
    ],
    value: idDocType,
  }));

  const handleBack = () => {
    const searchParams = new URLSearchParams(location.search);
    const backRouteUrlParam = searchParams.get('backRoute');

    if (backRouteUrlParam) {
      navigate(backRouteUrlParam);
      return;
    }

    switch (location.pathname) {
      case routes.FirstLoan_OnlineBank_AddDocuments:
        navigate(routes.FirstLoan_OnlineBank_DocumentsRequired);
        return;
      case routes.FirstLoan_Manual_Upload:
        navigate(routes.FirstLoan_Manual);
        return;
    }
  };

  const handleContinue = () => {
    switch (location.pathname) {
      case routes.FirstLoan_OnlineBank_AddDocuments:
        navigate(routes.FirstLoan_OnlineBank_Iban);
        return;
      case routes.FirstLoan_Manual_Upload:
        navigate(routes.FirstLoan_Manual_Upload_BankAccount);
        return;
      default:
        navigate(routes.FirstLoan_Manual_Upload_Pending);
        return;
    }
  };

  const showErrorRef = useRef(showError);

  const transformHeicImg = async (file: any) => {
    const outputBuffer = await heic2any({
      blob: file,
      toType: DESTINATION_FILE_TYPE,
      quality: 0.8, // Set the desired JPEG quality (0 to 1)
    }); // error check after//
    const newName = `${file.name.split('.')[0]}.${
      DESTINATION_FILE_TYPE.split('/')[1]
    }`;
    return new File([outputBuffer as Blob], newName, {
      type: DESTINATION_FILE_TYPE,
      lastModified: file.lastModified,
    });
  };

  const parseFile = async (fileFromProps: File | null) => {
    let file: File | null = fileFromProps;

    const nameTypeFile = file?.type || file?.name || '';
    const isHeicHeifFile =
      nameTypeFile.includes('heic') || nameTypeFile.includes('heif');

    if (isHeicHeifFile) {
      file = await transformHeicImg(file);
    }
    return file;
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!stateRef.current) return;

    const nameTypeDoc = event.target.name;
    const file = event.target.files ? event.target.files[0] : null;
    const parsedFile = await parseFile(file);

    updateErrorAndDocuments(nameTypeDoc, parsedFile);
  };

  const handleDrop = async (event: React.DragEvent<HTMLInputElement>) => {
    const fileDropped = event.dataTransfer.files[0];
    const parsedFile = await parseFile(fileDropped);

    updateErrorAndDocuments(fileDropped?.name, parsedFile);
  };

  const handleDelete = (name: string) => {
    formik.setFieldValue(name, null);

    updateErrorAndDocuments(name, null);
  };

  const handleRemove = useCallback((name: string) => {
    setDocumentsState((previousState) =>
      previousState.filter((doc) => doc.name !== name),
    );
  }, []);

  const defaultDocConfig = {
    onChange: handleChange,
    onDrop: handleDrop,
    onDelete: handleDelete,
    onRemoveHandler: handleRemove,
  };

  const formik = useFormik<FormFormik>({
    initialValues: {
      document: null,
      idBack: null,
      idFront: null,
    },
    enableReinitialize: true,
    onSubmit: (form: any) => {
      alert('File: ' + form);
    },
  });

  const initialDocs: UploadCardProps[] = [
    {
      ...defaultDocConfig,
      name: 'idFront',
      index: 0,
      type: 'frontSideDni',
      value: formik.values.idFront,
      title: i18Next.t('option1'),
      isTouched: formik.touched.idFront,
      error: !!formik.errors.idFront,
      onRemoveHandler: () => {},
    },
    {
      ...defaultDocConfig,
      index: 1,
      name: 'idBack',
      type: 'backSideDni',
      value: formik.values.idBack,
      title: i18Next.t('option2'),
      isTouched: formik.touched.idBack,
      error: !!formik.errors.idBack,
      onRemoveHandler: () => {},
    },
    {
      ...defaultDocConfig,
      index: 2,
      name: 'document',
      type: 'otherDocument',
      value: formik.values.document,
      title: i18Next.t('option3'),
      isTouched: formik.touched.document,
      error: !!formik.errors.document,
      onRemoveHandler: () => {},
      warning: i18Next.t('option3_warning'),
    },
  ];

  const [documentsState, setDocumentsState] = useState(initialDocs);

  const stateRef = useRef();

  // Ref used to get the last value of the state variable inside of a function
  // @ts-ignore
  stateRef.current = documentsState;

  const isFileValid = (file: File | null | undefined) => {
    if (!file) return true;
    const fileNameDestructured = file.name.split('.');
    const typeFile = file
      ? fileNameDestructured[fileNameDestructured.length - 1].toLowerCase()
      : '';

    const validTypeFile = !file || VALID_FILE_TYPES.includes(typeFile);

    return validTypeFile && !(file && file.size > MAX_FILE_SIZE);
  };

  const updateErrorAndDocuments = (
    name: string | undefined,
    file: File | null,
  ) => {
    const isValid = isFileValid(file);

    if (isValid) {
      formik.setFieldValue(name || 'image', file);
    }

    // @ts-ignore
    const nextDocuments: UploadCardProps[] = stateRef.current.map(
      (fileInfo: UploadCardProps) => {
        const { name: nameFile } = fileInfo;
        if (name === nameFile) {
          const error = !isFileValid(file);

          fileInfo.error = Boolean(error);
          fileInfo.isTouched = true;
          fileInfo.value = file;
        }
        return fileInfo;
      },
    );

    const nextShowError = nextDocuments.some(
      ({ value }) => !isFileValid(value),
    );
    if (nextShowError !== showErrorRef.current) {
      showErrorRef.current = nextShowError;
      setShowError(nextShowError);
    }
    setDocumentsState(nextDocuments);
  };

  const removeAdditionalDocument = (index: number) => {
    setDocumentsState(
      documentsState.filter((doc, indexDoc) => indexDoc !== index),
    );
  };

  const addNewDocument = (docType: DOC_TYPE, doc: File) => {
    if (documentsState.length > 5) {
      return;
    }

    const nextNumber = documentsState.length - 2;
    // @ts-ignore
    const name: FormikField = `additionalDocument${nextNumber}`;

    if (!formik.values[name]) {
      formik.values[name] = doc;
    }

    const nextDocuments = [
      ...documentsState,
      {
        ...defaultDocConfig,
        index: documentsState.length,
        name: name,
        type: docType,
        value: formik.values[name],
        title: i18Next.t(`documentTypes.${docType}`),
        isTouched: formik.touched[name],
        error: !!formik.errors[name],
        onRemoveHandler: () => removeAdditionalDocument(documentsState.length),
      },
    ];

    setDocumentsState(nextDocuments);
  };

  const closeModal = () => {
    setModalOpened(false);
  };

  const onContinueBtnHandler = async () => {
    const keys: FormikField[] = ['document', 'idBack', 'idFront'];
    keys.forEach((key: FormikField) => {
      const currentValue: any = formik.values[key];
      if (!currentValue) delete formik.values[key];
    });

    const body: FormData[] = [];
    documentsState.forEach(({ type, value }) => {
      if (value) {
        // Build formData
        const formData = new FormData();
        formData.append('file', value);
        formData.append('type', 'otherDocument');

        body.push(formData);
      }
    });
    try {
      setLoading(true);
      const url = `${getProcessEnvUrl('WEB_API')}/client/attachments`;
      for (const formData of body) {
        await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Basic ${sessionStorage.getItem('token')}`,
          },
        });
      }
      await fetchClient();
      handleContinue();
    } catch (error) {
      // return to this page (user was on loading screen)
      setLoading(false);
      const errors = error?.fieldErrors || error?.response?.data?.errors;
      if (errors?.[0].message.includes('File size')) {
        handleError({ name: 'fileSize' });
      } else {
        handleError(error);
      }
    }
  };

  const showFormatError = Boolean(documentsState.find((doc) => doc.error));
  const mainDocumentsAttached =
    documentsState.slice(0, 3).filter((doc) => !!doc.value).length === 3;
  const continueBtnDisabled = !mainDocumentsAttached || showFormatError;

  const ModalAdditionalDocStep1 = useMemo(() => {
    return (
      <Modal
        className={styles['modal-type']}
        isOpen={additionalDocModalInfo.step === 1}
        onClose={() => {
          setAdditionalDocModalInfo({
            ...additionalDocModalInfo,
            step: 0,
            docType: null,
          });
        }}
      >
        <div
          className={styles['modal-type-title']}
          data-test="uploadDocumentation-title"
        >
          {content.modalNewDocument.title}
        </div>
        <Select
          options={DOC_TYPES}
          onChange={(e) => {
            const nextType: DOC_TYPE = e.target.value;
            setAdditionalDocModalInfo({
              ...additionalDocModalInfo,
              docType: nextType,
            });
          }}
          label={content.modalNewDocument.selectPlaceholder}
          value={additionalDocModalInfo.docType || undefined}
        />
        <Button
          dataTest="uploadDocumentation-additional-btn"
          disabled={!Boolean(additionalDocModalInfo.docType)}
          className={styles['modal-type-submit']}
          onClick={() => {
            setAdditionalDocModalInfo({
              ...additionalDocModalInfo,
              step: 2,
            });
          }}
        >
          {content.modalNewDocument.submitBtn}
        </Button>
      </Modal>
    );
  }, [additionalDocModalInfo]);

  const ModalAdditionalDocStep2 = useMemo(() => {
    const onDocChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files[0] : null;
      setModalAddDocLoading(true);
      const parsedFile = await parseFile(file);
      setModalAddDocLoading(false);
      setAdditionalDocModalInfo({
        ...additionalDocModalInfo,
        doc: parsedFile,
      });
    };

    return (
      <Modal
        className={styles['modal-type']}
        isOpen={additionalDocModalInfo.step === 2}
        onClose={() => {
          setAdditionalDocModalInfo({
            ...additionalDocModalInfo,
            step: 0,
            docType: null,
            doc: null,
          });
        }}
      >
        <div
          className={styles['modal-type-title']}
          data-test="uploadDocumentation-title-second-step"
        >
          {`${content.modalNewDocument.titleSecondStep} ${
            content.documentTypes[
              additionalDocModalInfo.docType as keyof typeof content.documentTypes
            ]
          }`}
        </div>
        {Boolean(additionalDocModalInfo.doc) ? (
          <UploadCard
            className={styles['card-file-attached']}
            index={documentsState.length}
            name="additional-name"
            type="otherDocument"
            value={additionalDocModalInfo.doc}
            title={
              additionalDocModalInfo.docType
                ? content.documentTypes[
                    additionalDocModalInfo.docType as keyof typeof content.documentTypes
                  ]
                : ''
            }
            onRemoveHandler={() => {}}
            onDelete={() => {
              setAdditionalDocModalInfo({
                ...additionalDocModalInfo,
                doc: null,
              });
            }}
            onChange={() => {}}
          />
        ) : (
          <Card className={styles['card-new-doc']}>
            <div
              className={classes(
                styles['card-new-doc-link-wrapper'],
                modalAddDocLoading && styles.loading,
              )}
            >
              {modalAddDocLoading && (
                <Loader className={styles['loader-modal']} />
              )}
              <input
                className={classes(
                  styles['card-attach-file'],
                  modalAddDocLoading && styles.loading,
                )}
                onChange={onDocChange}
                type="file"
                accept="application/pdf,image/jpeg,image/gif,image/png,image/tiff,image/bmp"
                disabled={modalAddDocLoading}
              />
              <Link className={styles['link-new-doc']} onClick={() => {}}>
                {content.modalNewDocument.selectFile}
              </Link>
              {modalAddDocLoading ? (
                <UploadCloudDisabledIcon data-test="upload-icon-disabled" />
              ) : (
                <UploadCloudIcon data-test="upload-icon" />
              )}
            </div>
          </Card>
        )}
        <Button
          disabled={!Boolean(additionalDocModalInfo.doc)}
          className={styles['modal-type-submit']}
          onClick={() => {
            if (
              showAdditionalDocBtnHandler &&
              additionalDocModalInfo.docType &&
              additionalDocModalInfo.doc
            ) {
              addNewDocument(
                additionalDocModalInfo.docType,
                additionalDocModalInfo.doc,
              );
            }
            setAdditionalDocModalInfo({
              docType: null,
              doc: null,
              step: 0,
            });
          }}
        >
          {content.modalNewDocument.submitBtn}
        </Button>
      </Modal>
    );
  }, [additionalDocModalInfo, modalAddDocLoading]);

  const showAdditionalDocBtnHandler = documentsState.length < 5;

  if (loading) {
    return <RegistrationLoading />;
  }

  return (
    <>
      {ModalAdditionalDocStep1}
      {ModalAdditionalDocStep2}
      <Modal
        isOpen={modalOpened}
        onClose={closeModal}
        className={styles.modal}
        isMobileFullWidth
      >
        <DocumentPhotoIcon
          className={styles['modal-picture']}
          data-test="document-photo-icon"
          viewBox="0 0 343 252"
        />
        <Spacer padding="25px 20px">
          <div className={styles['modal-container']}>
            <div className={styles['modal-title']} data-test="document-title">
              {content.modalTitle}
            </div>
            <div className={styles['modal-body']} data-test="document-content">
              <ul>
                <li data-test="document-content-line-1">
                  {content.modalLine1}
                </li>
                <li data-test="document-content-line-2">
                  {content.modalLine2}
                </li>
                <li data-test="document-content-line-3">
                  {content.modalLine3}
                </li>
              </ul>
            </div>
          </div>
          <ButtonActor
            buttonProps={{
              dataTest: 'document-continue-btn',
              onClick: closeModal,
              className: styles['modal-button'],
              fill: true,
            }}
            className={styles['modal-button-container']}
          >
            {content.btnContinue}
          </ButtonActor>
        </Spacer>
      </Modal>
      <Frame
        {...frameConfig}
        hideMobileFooter
        hideLogout
        hideEditButton={true}
        sectionID={AppRegistrationSection.Confirmation}
        onBackBtnHandler={handleBack}
        bodyWrapperClassName={classNames(
          layout['frame-s-padding'],
          styles['frame-body-wrapper'],
          layout['width-lg'],
        )}
      >
        <Title text={content.title} />
        <Text
          level={2}
          className={styles['subtitle']}
          dataTest="documentation-subtitle"
          kind="p"
          text={content.subtitle}
        />
        <Spacer marginBottom={{ default: '20px', s: '15px' }}>
          <Card intent="warning" dataTest="documentation-card">
            <Spacer margin="12px 14px" className={styles['warning-card']}>
              <InformationIcon data-test="documentation-info-icon" />
              <Text
                level={2}
                className={styles['warning-card-text']}
                dataTest="documentation-warning-msg"
                dangerouslySetInnerHTML={{ __html: content.warningMessage }}
              />
            </Spacer>
          </Card>
        </Spacer>
        <div className={styles['upload-cards-wrapper']}>
          {documentsState.map((args, index) => (
            <>
              {args.warning && (
                <Card
                  intent="warning"
                  dataTest="documentation-card-warning"
                  className={styles['warning-top-card']}
                >
                  <Spacer margin="12px 14px" className={styles['warning-card']}>
                    <InformationIcon data-test="documentation-info-icon" />
                    <Text
                      level={2}
                      className={styles['warning-card-text']}
                      dataTest="documentation-warning-msg"
                      dangerouslySetInnerHTML={{ __html: args.warning }}
                    />
                  </Spacer>
                </Card>
              )}
              <UploadCard key={args.name} {...args} index={index} />
            </>
          ))}
        </div>
        <Spacer
          className={styles['card-group-bottom']}
          marginTop={{ s: '5px' }}
        >
          {showAdditionalDocBtnHandler && (
            <ButtonActor
              buttonProps={{
                className: styles['button-no-border'],
                dataTest: 'documentation-modal-info-btn',
                onClick: () => {
                  setAdditionalDocModalInfo({
                    ...additionalDocModalInfo,
                    step: 1,
                  });
                },
                outlined: true,
              }}
              width="auto"
              analytics={{
                contentOverride: `+ ${content.btnAdditionalDocument}`,
              }}
            >
              + {content.btnAdditionalDocument}
            </ButtonActor>
          )}
          <div className={styles.secured}>
            <LockIcon data-test="documentation-lock-icon" /> {content.secured}
          </div>
        </Spacer>
        {showFormatError && (
          <Spacer className={styles['error-upload']} marginBottom="20px">
            <WarningRedIcon data-test="documentation-warning-red-icon" />
            <Text
              text={content.formatError}
              dataTest="documentation-format-error"
            />
          </Spacer>
        )}
        <div
          className={classNames(styles['wrapper-buttons'], layout['width-sm'])}
        >
          <Button
            disabled={continueBtnDisabled}
            loading={loading}
            onClick={onContinueBtnHandler}
            className={classes([styles['continue-button'], JUICY_NEXT_BTN])}
            dataTest="documentation-continue-btn"
          >
            {content.btnContinue}
          </Button>
          <Spacer marginTop="40px">
            <Link
              dataTest="documentation-link"
              leftIcon={
                <ArrowLeftIcon data-test="documentation-arrow-left-icon" />
              }
              onClick={handleBack}
            >
              {content.btnBack}
            </Link>
          </Spacer>
        </div>
      </Frame>
    </>
  );
}
